import { CatalogGraphPage, Direction } from '@backstage/plugin-catalog-graph';
import { catalogGraphRouteRef } from '@backstage/plugin-catalog-graph';
import { stringifyEntityRef, type Entity } from '@backstage/catalog-model';
import { Progress, Select } from '@backstage/core-components';
import {
  attachComponentData,
  configApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Box from '@material-ui/core/Box';
import { useAsync } from 'react-use';
import { RdapCatalogNode, useCompactGraphLabels } from './CatalogGraphFocus';

const DEFAULT_CATALOG_GRAPH_STATE = {
  selectedKinds: [
    'Tenant',
    'Domain',
    'System',
    'Component',
    'API',
    'Resource',
    'Template',
  ],
  selectedRelations: [
    'hasPart',
    'partOf',
    'dependsOn',
    'dependencyOf',
    'providesApi',
    'apiProvidedBy',
    'consumesApi',
    'apiConsumedBy',
    'ownedBy',
    'ownerOf',
  ],
  maxDepth: 3,
  unidirectional: true,
  mergeRelations: true,
  direction: Direction.LEFT_RIGHT,
  showFilters: true,
} as const;

const ALL_TENANTS = '*';

type TenantOption = { ref: string; title: string };

/**
 * Optional Tenant scope control for the Catalog Graph filter panel.
 *
 * The upstream {@link CatalogGraphPage} owns that panel and exposes no slot for
 * extra filters, so the control is portalled into the panel's DOM node (React
 * context is preserved) and re-attached whenever the panel is toggled or
 * re-rendered.
 */
function TenantScopeFilter({
  tenants,
  value,
  onChange,
}: {
  tenants: { ref: string; title: string }[];
  value: string;
  onChange: (value: string) => void;
}) {
  const [host, setHost] = useState<HTMLElement | null>(null);

  useEffect(() => {
    let frame = 0;
    const sync = () => {
      frame = 0;
      const panel = document.querySelector<HTMLElement>(
        '[class*="CatalogGraphPage-filters-"]',
      );
      if (!panel) {
        setHost(current => (current ? null : current));
        return;
      }
      let mount = panel.querySelector<HTMLElement>(
        ':scope > [data-rdap-tenant-filter]',
      );
      if (!mount) {
        mount = document.createElement('div');
        mount.dataset.rdapTenantFilter = 'true';
        panel.insertBefore(mount, panel.firstChild);
      }
      setHost(current => (current === mount ? current : mount));
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(sync);
    };
    sync();
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  if (!host) return null;

  // The Backstage Select renders an empty field for an empty-string value, so
  // the "all tenants" option needs a non-empty sentinel that maps back to "".
  return createPortal(
    <Box pb={1} pt={1}>
      <Select
        label="Tenant"
        selected={value || ALL_TENANTS}
        items={[
          { label: 'All tenants', value: ALL_TENANTS },
          ...tenants.map(tenant => ({ label: tenant.title, value: tenant.ref })),
        ]}
        onChange={selected =>
          onChange(selected === ALL_TENANTS ? '' : String(selected))
        }
      />
    </Box>,
    host,
  );
}

export function RdapCatalogGraphPage() {
  const graphContainerRef = useRef<HTMLDivElement>(null);
  useCompactGraphLabels(graphContainerRef);
  const location = useLocation();
  const navigate = useNavigate();
  const configStateHasBeenApplied = useRef(false);
  const [waitingForConfiguredState, setWaitingForConfiguredState] = useState(
    () => hasPersistedGraphState(location.search),
  );
  const configApi = useApi(configApiRef);
  const catalogApi = useApi(catalogApiRef);
  const graphConfig = configApi.getOptionalConfig('rdap.knowledgeGraph');
  const configuredRoots = graphConfig?.getOptionalStringArray('rootEntityRefs');
  const [selectedTenantRef, setSelectedTenantRef] = useState('');

  const { value: tenantEntities, loading } = useAsync(
    () =>
      catalogApi
        .getEntities({ filter: { kind: 'Tenant' }, limit: 5000 })
        .then(response => response.items),
    [catalogApi],
  );
  const { value: catalogKinds, loading: kindsLoading } = useAsync(
    () =>
      catalogApi
        .getEntityFacets({ facets: ['kind'] })
        .then(response => response.facets.kind?.map(facet => facet.value.toLocaleLowerCase('en-US')) ?? []),
    [catalogApi],
  );
  const tenantOptions = useMemo(
    () =>
      ((tenantEntities ?? []) as Entity[])
        .map((entity: Entity): TenantOption => ({
          ref: stringifyEntityRef(entity),
          title: entity.metadata.title ?? entity.metadata.name,
        }))
        .sort((a: TenantOption, b: TenantOption) => a.title.localeCompare(b.title)),
    [tenantEntities],
  );
  const rootEntityRefs = useMemo(
    () =>
      configuredRoots?.length
        ? configuredRoots
        : tenantOptions.map((tenant: TenantOption) => tenant.ref),
    [configuredRoots?.join('|'), tenantOptions],
  );

  // Optional tenant scope: restrict the graph to a single Tenant's hasPart
  // subtree. An empty selection leaves every configured root visible.
  const { value: tenantScopeRefs } = useAsync(async () => {
    if (!selectedTenantRef) return undefined;
    const allowed = new Set<string>([selectedTenantRef]);
    let frontier = [selectedTenantRef];
    while (frontier.length > 0) {
      const { items } = await catalogApi.getEntitiesByRefs({
        entityRefs: frontier,
      });
      const next: string[] = [];
      for (const entity of items) {
        if (!entity) continue;
        for (const relation of entity.relations ?? []) {
          if (relation.type !== 'hasPart') continue;
          if (!allowed.has(relation.targetRef)) {
            allowed.add(relation.targetRef);
            next.push(relation.targetRef);
          }
        }
      }
      frontier = next;
    }
    return allowed;
  }, [catalogApi, selectedTenantRef]);
  const entityFilter = useCallback(
    (entity: Entity) =>
      !tenantScopeRefs || tenantScopeRefs.has(stringifyEntityRef(entity)),
    [tenantScopeRefs],
  );

  const selectedEntityRefs = new URLSearchParams(location.search).getAll(
    'rootEntityRefs[]',
  );

  // CatalogGraphPage initializes its state once, giving URL values priority over
  // initialState. Do not mount it until stale filter values are removed, or it
  // will retain the old TB/depth-2/kinds state for the lifetime of the page.
  useEffect(() => {
    if (configStateHasBeenApplied.current) return;
    const query = new URLSearchParams(location.search);
    for (const key of [...query.keys()]) {
      if (isPersistedGraphStateKey(key)) query.delete(key);
    }
    const search = query.toString();
    if (search !== location.search.replace(/^\?/, '')) {
      navigate(search ? `?${search}` : '', { replace: true });
      return;
    }
    configStateHasBeenApplied.current = true;
    setWaitingForConfiguredState(false);
  }, [location.search, navigate]);

  if (loading || kindsLoading || !rootEntityRefs || waitingForConfiguredState)
    return <Progress />;

  const configuredKinds = graphConfig?.getOptionalStringArray('kinds') ?? [
    ...DEFAULT_CATALOG_GRAPH_STATE.selectedKinds,
  ];
  const selectedKinds = catalogKinds?.length
    ? configuredKinds.filter(kind =>
        catalogKinds.includes(kind.toLocaleLowerCase('en-US')),
      )
    : configuredKinds;

  return (
    <div className="rdap-catalog-graph-page" ref={graphContainerRef}>
      <TenantScopeFilter
        tenants={tenantOptions}
        value={selectedTenantRef}
        onChange={setSelectedTenantRef}
      />
      <CatalogGraphPage
        entityFilter={entityFilter}
        initialState={{
          rootEntityRefs,
          selectedKinds,
          selectedRelations: graphConfig?.getOptionalStringArray(
            'relations',
          ) ?? [...DEFAULT_CATALOG_GRAPH_STATE.selectedRelations],
          maxDepth:
            graphConfig?.getOptionalNumber('maxDepth') ??
            DEFAULT_CATALOG_GRAPH_STATE.maxDepth,
          unidirectional:
            graphConfig?.getOptionalBoolean('unidirectional') ??
            DEFAULT_CATALOG_GRAPH_STATE.unidirectional,
          mergeRelations:
            graphConfig?.getOptionalBoolean('mergeRelations') ??
            DEFAULT_CATALOG_GRAPH_STATE.mergeRelations,
          direction:
            (graphConfig?.getOptionalString('direction') as
              | Direction
              | undefined) ?? DEFAULT_CATALOG_GRAPH_STATE.direction,
          showFilters:
            graphConfig?.getOptionalBoolean('showFilters') ??
            DEFAULT_CATALOG_GRAPH_STATE.showFilters,
        }}
        showArrowHeads
        renderNode={props => (
          <RdapCatalogNode {...props} selectedEntityRefs={selectedEntityRefs} />
        )}
      />
    </div>
  );
}

// The page is rendered with the Catalog Graph plugin route. This also binds
// its route reference for the standard Catalog Graph entity card.
attachComponentData(
  RdapCatalogGraphPage,
  'core.extensionName',
  'CatalogGraphPage',
);
attachComponentData(
  RdapCatalogGraphPage,
  'core.mountPoint',
  catalogGraphRouteRef,
);

const GRAPH_STATE_KEYS = [
  'maxDepth',
  'selectedKinds',
  'selectedRelations',
  'unidirectional',
  'mergeRelations',
  'direction',
  'showFilters',
  'curve',
];

function isPersistedGraphStateKey(key: string) {
  return GRAPH_STATE_KEYS.some(
    stateKey => key === stateKey || key.startsWith(`${stateKey}[`),
  );
}

function hasPersistedGraphState(search: string) {
  return [...new URLSearchParams(search).keys()].some(isPersistedGraphStateKey);
}
